import { ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { showToast } from '@components/toaster';
import { isOpeningStep, sequenceChannel, TrackerConfig } from 'shared/tracker-config';
import {
  betOf,
  emptyPersonFields,
  FIELD_LIMITS,
  findDuplicatePerson,
  fmtTrackerDate,
  MAX_PEOPLE_PER_PASTE,
  personName,
  personOfferText,
  PICK_A_BET_FOR_LIST_MESSAGE,
  PICK_A_BET_MESSAGE,
  stageLabel,
  TrackerPersonFields,
} from 'shared/tracker-people';
import { parsePastedPeople } from 'shared/tracker-paste';
import { MAX_IMPORT_PEOPLE, parseRisImport } from 'shared/tracker-import';
import { hasPlanSetup, parsePlanImport, planSetupLines } from 'shared/tracker-plan-import';
import { FILE_TOO_BIG_MESSAGE, MAX_IMPORT_CSV_BYTES } from 'shared/tracker-copy';
import { FieldError, Panel, StepHeading } from './panel';
import {
  HELP_CLASS,
  INPUT_CLASS,
  LABEL_CLASS,
  LINK_CLASS,
  Person,
  PersonResult,
  readError,
  readLocal,
  TrackerData,
  writeLocal,
} from './helpers';
import { BetSelect, defaultBetId, rememberBet } from './bets';

function lastChannelKey(courseId: string) {
  return `tracker-last-channel:${courseId}`;
}

function lastStepKey(courseId: string) {
  return `tracker-last-first-touch:${courseId}`;
}

function addAnotherKey(courseId: string) {
  return `tracker-add-another:${courseId}`;
}

function defaultChannel(config: TrackerConfig, courseId: string) {
  const saved = readLocal(lastChannelKey(courseId));
  return config.channels.find((c) => c.key === saved)?.key ?? config.channels[0]?.key ?? '';
}

function defaultFirstStep(config: TrackerConfig, courseId: string) {
  const openings = config.sequence.filter(isOpeningStep);
  const saved = readLocal(lastStepKey(courseId));
  return openings.find((s) => s.key === saved)?.key ?? openings[0]?.key;
}

function dayText(on: string, today: string) {
  return on === today ? 'today' : fmtTrackerDate(on, today);
}

function ChannelSelect({
  config,
  value,
  onChange,
}: {
  config: TrackerConfig;
  value: string;
  onChange: (key: string) => void;
}) {
  if (config.channels.length < 2) {
    return;
  }
  return (
    <label class="flex flex-col gap-1">
      <span class={LABEL_CLASS}>Channel</span>
      <select class={INPUT_CLASS} value={value} onChange={(e) => onChange(e.currentTarget.value)}>
        {config.channels.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function AddPersonPanel({
  data,
  courseId,
  onAdded,
  openPerson,
  close,
}: {
  data: TrackerData;
  courseId: UUID;
  onAdded: (result: PersonResult) => void;
  openPerson: (personId: string) => void;
  close: () => void;
}) {
  const { config, today } = data;
  const fields = config.fields;
  const [values, setValues] = useState(emptyPersonFields);
  const [channelKey, setChannelKey] = useState(() => defaultChannel(config, courseId));
  const [on, setOn] = useState(today);
  const [stepKey, setStepKey] = useState(() => defaultFirstStep(config, courseId));
  const [betId, setBetId] = useState(() => defaultBetId(data.bets, courseId));
  const [betError, setBetError] = useState<string | undefined>();
  const [addAnother, setAddAnother] = useState(() => readLocal(addAnotherKey(courseId)) === '1');
  const [moreOpen, setMoreOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const firstRef = useRef<HTMLInputElement>(null);
  const channel = config.channels.find((c) => c.key === channelKey);
  const isSequence = channel?.flow === 'sequence';
  const offers = useMemo(
    () => [...new Set(data.people.map((p) => p.offer.trim()).filter(Boolean))].sort(),
    [data.people],
  );
  const candidate = {
    ...values,
    fullName: values.fullName || values.firstName,
    betId: config.bets ? betId : undefined,
  };
  const duplicate =
    values.firstName.trim() && (!config.bets || betId)
      ? findDuplicatePerson(candidate, data.people)
      : undefined;

  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  function set(key: keyof TrackerPersonFields, value: string) {
    setValues((v) => ({ ...v, [key]: value }));
  }

  async function save(e: Event) {
    e.preventDefault();
    if (!values.firstName.trim()) {
      setError('Add a first name.');
      firstRef.current?.focus();
      return;
    }
    if (config.bets && !betId) {
      setBetError(PICK_A_BET_MESSAGE);
      return;
    }
    setSaving(true);
    setError(undefined);
    setBetError(undefined);
    try {
      const result = await rpx.tracker.addPerson({
        courseId,
        person: values,
        channelKey,
        firstTouchOn: on,
        firstStepKey: isSequence ? stepKey : undefined,
        betId: config.bets ? (betId as UUID) : undefined,
      });
      rememberBet(courseId, betId);
      writeLocal(lastChannelKey(courseId), channelKey);
      if (stepKey) {
        writeLocal(lastStepKey(courseId), stepKey);
      }
      onAdded(result);
      const nextName = result.person.nextStep;
      showToast({
        type: 'ok',
        title: `Added ${personName(result.person)}${nextName ? ` · ${nextName} ${dayText(on, today)}` : ''}`,
        message: '',
      });
      if (addAnother) {
        setValues(emptyPersonFields());
        firstRef.current?.focus();
      } else {
        close();
      }
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  function input(key: keyof TrackerPersonFields, label: string, help?: string) {
    return (
      <label class="flex flex-col gap-1">
        <span class={LABEL_CLASS}>{label}</span>
        <input
          class={INPUT_CLASS}
          maxLength={FIELD_LIMITS[key]}
          value={values[key]}
          onInput={(e) => set(key, e.currentTarget.value)}
        />
        {help && <span class={HELP_CLASS}>{help}</span>}
      </label>
    );
  }

  return (
    <Panel
      title="Add person"
      close={close}
      footer={
        <>
          <BtnPrimary type="submit" form="tracker-add-person" isLoading={saving}>
            {duplicate ? 'Add anyway' : 'Add person'}
          </BtnPrimary>
          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={addAnother}
              onChange={(e) => {
                setAddAnother(e.currentTarget.checked);
                writeLocal(addAnotherKey(courseId), e.currentTarget.checked ? '1' : '0');
              }}
            />
            Add another
          </label>
        </>
      }
    >
      <form id="tracker-add-person" class="flex flex-col gap-4" onSubmit={save}>
        {config.bets && (
          <BetSelect
            bets={data.bets}
            value={betId}
            error={betError}
            onChange={(id) => {
              setBetId(id);
              setBetError(undefined);
            }}
          />
        )}
        <label class="flex flex-col gap-1">
          <span class={LABEL_CLASS}>First name</span>
          <input
            ref={firstRef}
            class={`${INPUT_CLASS} ${error ? 'border-red-500' : ''}`}
            maxLength={FIELD_LIMITS.firstName}
            value={values.firstName}
            aria-describedby={error ? 'add-person-error' : undefined}
            onInput={(e) => set('firstName', e.currentTarget.value)}
          />
          <span class={HELP_CLASS}>Used in your messages</span>
        </label>
        {input('fullName', 'Full name (optional)')}
        {fields.offer && !config.bets && (
          <label class="flex flex-col gap-1">
            <span class={LABEL_CLASS}>Offer</span>
            <input
              class={INPUT_CLASS}
              list="tracker-offers"
              maxLength={FIELD_LIMITS.offer}
              value={values.offer}
              onInput={(e) => set('offer', e.currentTarget.value)}
            />
            <datalist id="tracker-offers">
              {offers.map((o) => (
                <option key={o} value={o} />
              ))}
            </datalist>
          </label>
        )}
        <ChannelSelect config={config} value={channelKey} onChange={setChannelKey} />
        <label class="flex flex-col gap-1">
          <span class={LABEL_CLASS}>Send the first touch on</span>
          <input
            type="date"
            class={`${INPUT_CLASS} max-w-44`}
            value={on}
            onChange={(e) => setOn(e.currentTarget.value || today)}
          />
        </label>
        {isSequence && (
          <label class="flex flex-col gap-1">
            <span class={LABEL_CLASS}>First touch</span>
            <select
              class={INPUT_CLASS}
              value={stepKey}
              onChange={(e) => setStepKey(e.currentTarget.value)}
            >
              {config.sequence.filter(isOpeningStep).map((s) => (
                <option key={s.key} value={s.key}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {fields.note && (
          <label class="flex flex-col gap-1">
            <span class={LABEL_CLASS}>Relationship note (optional)</span>
            <textarea
              class={INPUT_CLASS}
              rows={3}
              maxLength={FIELD_LIMITS.note}
              value={values.note}
              onInput={(e) => set('note', e.currentTarget.value)}
            />
            <span class={HELP_CLASS}>Used to write the personal opener</span>
          </label>
        )}
        {(fields.email || fields.phone) && (
          <div class="flex flex-col gap-2">
            {fields.email && input('email', 'Email (optional)')}
            {fields.phone && input('phone', 'Phone (optional)')}
            <span class={HELP_CLASS}>
              You send from your own email and phone. These are for your reference only.
            </span>
          </div>
        )}
        {(fields.segment || fields.tier) && (
          <div class="flex flex-col gap-3">
            <Button
              class={`${LINK_CLASS} self-start`}
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen(!moreOpen)}
            >
              More details
            </Button>
            {moreOpen && (
              <>
                {fields.segment && input('segment', 'Segment')}
                {fields.tier && input('tier', 'Tier')}
              </>
            )}
          </div>
        )}
        {duplicate && (
          <p class="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-md p-3">
            You already have {personName(duplicate)}
            {personOfferText(config, data.bets, duplicate)
              ? ` for ${personOfferText(config, data.bets, duplicate)}`
              : ''}{' '}
            ({stageLabel(config, duplicate.stageKey)}).{' '}
            <Button class={LINK_CLASS} onClick={() => openPerson(duplicate.id)}>
              Open
            </Button>
          </p>
        )}
        <FieldError id="add-person-error" message={error} />
      </form>
    </Panel>
  );
}

export function PastePanel({
  data,
  courseId,
  onAdded,
  close,
}: {
  data: TrackerData;
  courseId: UUID;
  onAdded: (people: Person[]) => void;
  close: () => void;
}) {
  const { config, today } = data;
  const [text, setText] = useState('');
  const [on, setOn] = useState(today);
  const [channelKey, setChannelKey] = useState(() => defaultChannel(config, courseId));
  const [betId, setBetId] = useState(() => defaultBetId(data.bets, courseId));
  const [betError, setBetError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const parsed = useMemo(() => parsePastedPeople(text), [text]);
  const batchBetId = config.bets ? betId : undefined;
  const batchBet = betOf(data.bets, batchBetId);
  const { fresh, skipped } = useMemo(() => {
    type Pasted = TrackerPersonFields & { betId?: string };
    const awaitingBet = config.bets && !batchBetId;
    const seen: Pasted[] = awaitingBet ? [] : [...data.people];
    const result = { fresh: [] as Pasted[], skipped: [] as Pasted[] };
    for (const pasted of parsed.people) {
      const person = { ...pasted, betId: batchBetId };
      if (findDuplicatePerson(person, seen)) {
        result.skipped.push(person);
      } else {
        seen.push(person);
        result.fresh.push(person);
      }
    }
    return result;
  }, [parsed, data.people, batchBetId, config.bets]);
  const tooMany = fresh.length > MAX_PEOPLE_PER_PASTE;
  const isSequence = config.channels.find((c) => c.key === channelKey)?.flow === 'sequence';

  async function save(e: Event) {
    e.preventDefault();
    if (!fresh.length || tooMany) {
      return;
    }
    if (config.bets && !betId) {
      setBetError(PICK_A_BET_FOR_LIST_MESSAGE);
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const result = await rpx.tracker.addPeople({
        courseId,
        people: fresh.map(({ betId: _, ...fields }) => fields),
        channelKey,
        firstTouchOn: on,
        firstStepKey: isSequence ? defaultFirstStep(config, courseId) : undefined,
        betId: batchBetId as UUID | undefined,
      });
      rememberBet(courseId, batchBetId);
      onAdded(result.people);
      showToast({
        type: 'ok',
        title: `Added ${result.people.length} ${result.people.length === 1 ? 'person' : 'people'}`,
        message: '',
      });
      close();
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel
      title="Paste a list"
      close={close}
      footer={
        <>
          <BtnPrimary
            type="submit"
            form="tracker-paste"
            disabled={!fresh.length || tooMany}
            isLoading={saving}
          >
            Add {fresh.length} {fresh.length === 1 ? 'person' : 'people'}
            {batchBet ? ` to ${batchBet.name}` : ''}
          </BtnPrimary>
          <BtnSecondary type="button" onClick={close}>
            Cancel
          </BtnSecondary>
        </>
      }
    >
      <form id="tracker-paste" class="flex flex-col gap-4" onSubmit={save}>
        {config.bets && (
          <BetSelect
            bets={data.bets}
            value={betId}
            label="Bet for everyone in this list"
            error={betError}
            onChange={(id) => {
              setBetId(id);
              setBetError(undefined);
            }}
          />
        )}
        <label class="flex flex-col gap-1">
          <span class={LABEL_CLASS}>Rows from a sheet, email list or notes</span>
          <textarea
            class={`${INPUT_CLASS} font-mono`}
            rows={8}
            placeholder={
              config.bets
                ? 'One person per line: name, email'
                : 'One person per line: name, email, offer'
            }
            value={text}
            autoFocus
            onInput={(e) => setText(e.currentTarget.value)}
          />
        </label>
        <ChannelSelect config={config} value={channelKey} onChange={setChannelKey} />
        <label class="flex flex-col gap-1">
          <span class={LABEL_CLASS}>Send the first touch on</span>
          <input
            type="date"
            class={`${INPUT_CLASS} max-w-44`}
            value={on}
            onChange={(e) => setOn(e.currentTarget.value || today)}
          />
        </label>
        {(parsed.people.length > 0 || parsed.noName > 0) && (
          <div class="flex flex-col gap-2">
            <p class="text-sm text-gray-900 dark:text-gray-100">
              {fresh.length} {fresh.length === 1 ? 'person' : 'people'} found
              {skipped.length > 0 && ` · ${skipped.length} already in your tracker (skipped)`}
            </p>
            {parsed.noName > 0 && (
              <p class={HELP_CLASS}>
                {parsed.noName} {parsed.noName === 1 ? 'row had' : 'rows had'} no name and{' '}
                {parsed.noName === 1 ? 'was' : 'were'} skipped
              </p>
            )}
            {tooMany && (
              <p class="text-sm text-red-700 dark:text-red-400">
                Paste up to {MAX_PEOPLE_PER_PASTE} people at a time. Split the list and paste the
                rest after.
              </p>
            )}
            <PreviewTable
              rows={fresh.map((p) =>
                config.bets ? [personName(p), p.email] : [personName(p), p.email, p.offer],
              )}
              headers={config.bets ? ['Name', 'Email'] : ['Name', 'Email', 'Offer']}
            />
            {skipped.length > 0 && (
              <details class="text-sm">
                <summary class="cursor-pointer text-indigo-600 dark:text-indigo-400">
                  Show skipped
                </summary>
                <ul class="mt-2 list-disc pl-5 text-gray-700 dark:text-gray-300">
                  {skipped.map((p, i) => (
                    <li key={i}>{personName(p)}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        <FieldError id="paste-error" message={error} />
      </form>
    </Panel>
  );
}

function PreviewTable({ rows, headers }: { rows: string[][]; headers: string[] }) {
  if (!rows.length) {
    return;
  }
  return (
    <div class="flex flex-col gap-1">
      <div class="border border-gray-200 dark:border-gray-700 rounded-md">
        <table class="w-full table-fixed text-sm">
          <thead class="bg-gray-50 dark:bg-gray-800/50 text-left text-xs text-gray-600 dark:text-gray-400">
            <tr>
              {headers.map((h) => (
                <th key={h} class="px-3 py-2 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.slice(0, 6).map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    class="px-3 py-2 text-gray-900 dark:text-gray-100 align-top break-words"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 6 && <p class={HELP_CLASS}>and {rows.length - 6} more</p>}
    </div>
  );
}

function CsvPicker({
  id,
  fileName,
  onFile,
}: {
  id: string;
  fileName?: string;
  onFile: (file?: { name: string; text: string }) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | undefined>();
  function read(file?: File) {
    if (!file) {
      return;
    }
    file.text().then((text) => {
      if (new Blob([JSON.stringify(text)]).size > MAX_IMPORT_CSV_BYTES) {
        setError(FILE_TOO_BIG_MESSAGE);
        onFile(undefined);
        return;
      }
      setError(undefined);
      onFile({ name: file.name, text });
    });
  }
  return (
    <div
      class={`flex flex-col gap-2 border-2 border-dashed rounded-md p-4 ${
        dragging
          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950'
          : 'border-gray-300 dark:border-gray-600'
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        read(e.dataTransfer?.files?.[0]);
      }}
    >
      <input
        id={id}
        type="file"
        accept=".csv,text/csv"
        class="text-sm"
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => read(e.currentTarget.files?.[0])}
      />
      {fileName && <span class={HELP_CLASS}>{fileName}</span>}
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

type ImportSource = 'plan' | 'sheet';

type ImportProps = {
  data: TrackerData;
  courseId: UUID;
  onImported: () => void;
  close: () => void;
};

/** What the participant imports: the plan file from their strategy weeks, or the old RIS sheet. */
function SourcePicker({
  value,
  onChange,
}: {
  value: ImportSource;
  onChange: (source: ImportSource) => void;
}) {
  const options: Array<{ value: ImportSource; label: string; help: string }> = [
    {
      value: 'plan',
      label: 'Your plan file',
      help: 'The tracker file from your plan: your target, bets and people.',
    },
    {
      value: 'sheet',
      label: 'Your RIS Google Sheet',
      help: 'The Outreach and Pipeline tabs, downloaded as .csv files.',
    },
  ];
  return (
    <fieldset class="flex flex-col gap-2" role="radiogroup">
      <legend class={`${LABEL_CLASS} mb-1`}>What are you importing?</legend>
      {options.map((o) => (
        <label key={o.value} class="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="import-source"
            class="mt-1"
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
          <span>
            <span class="font-medium">{o.label}</span>
            <span class="block text-gray-600 dark:text-gray-400">{o.help}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

export function ImportPanel(props: ImportProps) {
  const [source, setSource] = useState<ImportSource>('plan');
  const picker = <SourcePicker value={source} onChange={setSource} />;
  return source === 'plan' ? (
    <PlanImportPanel {...props} picker={picker} />
  ) : (
    <SheetImportPanel {...props} picker={picker} />
  );
}

/**
 * A plan file: setting rows, then people. Every setting the file would
 * change is listed with what it replaces, and the import waits for the
 * participant to tick that they checked them.
 */
function PlanImportPanel({
  data,
  courseId,
  onImported,
  close,
  picker,
}: ImportProps & { picker: ComponentChildren }) {
  const { config, today } = data;
  const [file, setFile] = useState<{ name: string; text: string } | undefined>();
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const preview = useMemo(
    () =>
      file
        ? parsePlanImport({
            config,
            csv: file.text,
            existing: data.people,
            bets: data.bets,
            participant: data.participant,
          })
        : undefined,
    [file, config, data.people, data.bets, data.participant],
  );
  const lines = preview ? planSetupLines(preview.setup, data.participant) : [];
  const count = preview?.people.length ?? 0;
  const tooMany = count > MAX_IMPORT_PEOPLE;
  const ready =
    !!preview &&
    !preview.fileError &&
    !preview.setupProblems.length &&
    (count > 0 || hasPlanSetup(preview.setup)) &&
    !tooMany &&
    (!lines.length || confirmed);

  async function save(e: Event) {
    e.preventDefault();
    if (!ready || !file) {
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const result = await rpx.tracker.importPlan({ courseId, csv: file.text });
      showToast({
        type: 'ok',
        title: `Imported ${result.imported} ${result.imported === 1 ? 'person' : 'people'}`,
        message: lines.length ? 'Your setup is saved too.' : '',
      });
      onImported();
      close();
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  const summary = preview
    ? [
        `${count} ${count === 1 ? 'person' : 'people'} found`,
        preview.duplicates.length
          ? `${preview.duplicates.length} already in your tracker (skipped)`
          : '',
        preview.noName
          ? `${preview.noName} ${preview.noName === 1 ? 'row' : 'rows'} with no name skipped`
          : '',
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Panel
      title="Import"
      close={close}
      footer={
        <>
          <BtnPrimary type="submit" form="tracker-import-plan" disabled={!ready} isLoading={saving}>
            {count || !lines.length
              ? `Import ${count} ${count === 1 ? 'person' : 'people'}`
              : 'Save setup'}
          </BtnPrimary>
          <BtnSecondary type="button" onClick={close}>
            Cancel
          </BtnSecondary>
        </>
      }
    >
      <form id="tracker-import-plan" class="flex flex-col gap-6" onSubmit={save}>
        {picker}
        <section class="flex flex-col gap-2">
          <StepHeading n={1}>Plan file</StepHeading>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Choose the tracker file from your plan. It’s a .csv file.
          </p>
          <CsvPicker
            id="import-plan"
            fileName={file?.name}
            onFile={(f) => {
              setFile(f);
              setConfirmed(false);
            }}
          />
        </section>
        {preview?.fileError && (
          <p class="text-sm text-red-700 dark:text-red-400">{preview.fileError}</p>
        )}
        {preview && !preview.fileError && preview.setupProblems.length > 0 && (
          <div class="flex flex-col gap-1">
            <h4 class="text-sm font-medium text-red-700 dark:text-red-400">
              Fix {preview.setupProblems.length === 1 ? 'this' : 'these'} in the file, then choose
              it again
            </h4>
            <ul class="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300">
              {preview.setupProblems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        )}
        {preview && !preview.fileError && !preview.setupProblems.length && lines.length > 0 && (
          <section class="flex flex-col gap-2">
            <StepHeading n={2}>Check your setup</StepHeading>
            <p class="text-sm text-gray-700 dark:text-gray-300">
              Your scoreboard and weekly closes run on these. Check each one against your plan.
            </p>
            <dl class="flex flex-col gap-2 border border-gray-200 dark:border-gray-700 rounded-md p-3 text-sm">
              {lines.map((line) => (
                <div key={line.label} class="flex flex-col">
                  <dt class="text-gray-600 dark:text-gray-400">{line.label}</dt>
                  <dd class="text-gray-900 dark:text-gray-100">
                    {line.value}
                    {line.was && (
                      <span class="block text-amber-800 dark:text-amber-300">
                        Replaces {line.was}
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            <label class="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.currentTarget.checked)}
              />
              I’ve checked these
            </label>
          </section>
        )}
        {preview && !preview.fileError && !preview.setupProblems.length && (
          <section class="flex flex-col gap-2">
            <StepHeading n={lines.length ? 3 : 2}>People</StepHeading>
            <p class="text-sm text-gray-900 dark:text-gray-100">{summary}</p>
            {tooMany && (
              <p class="text-sm text-red-700 dark:text-red-400">
                Import up to {MAX_IMPORT_PEOPLE} people at a time. Split the file and import the
                rest after.
              </p>
            )}
            <PreviewTable
              headers={config.bets ? ['Name', 'Bet', 'First touch'] : ['Name', 'First touch']}
              rows={preview.people.map((p) => [
                personName(p),
                ...(config.bets ? [p.betName ?? ''] : []),
                fmtTrackerDate(p.nextOn, today),
              ])}
            />
            {preview.problems.length > 0 && (
              <div class="flex flex-col gap-1">
                <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {preview.problems.length}{' '}
                  {preview.problems.length === 1 ? 'row problem' : 'row problems'} (left out)
                </h4>
                <ul class="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300">
                  {preview.problems.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
        <FieldError id="import-plan-error" message={error} />
      </form>
    </Panel>
  );
}

function SheetImportPanel({
  data,
  courseId,
  onImported,
  close,
  picker,
}: ImportProps & { picker: ComponentChildren }) {
  const { config, today } = data;
  const [outreach, setOutreach] = useState<{ name: string; text: string } | undefined>();
  const [pipeline, setPipeline] = useState<{ name: string; text: string } | undefined>();
  const [fallbackBetId, setFallbackBetId] = useState(() => defaultBetId(data.bets, courseId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const preview = useMemo(
    () =>
      outreach
        ? parseRisImport({
            config,
            outreachCsv: outreach.text,
            pipelineCsv: pipeline?.text,
            today,
            existing: data.people,
            bets: data.bets,
            fallbackBetId,
          })
        : undefined,
    [outreach, pipeline, config, today, data.people, data.bets, fallbackBetId],
  );
  const count = preview?.people.length ?? 0;
  const matched = preview?.people.filter((p) => p.matchedBet).length ?? 0;
  const unmatched = config.bets ? count - matched : 0;
  const tooMany = count > MAX_IMPORT_PEOPLE;
  const ready =
    !!preview && !preview.fileError && count > 0 && !tooMany && (!unmatched || !!fallbackBetId);
  const hasSequence = !!sequenceChannel(config);

  async function save(e: Event) {
    e.preventDefault();
    if (!ready || !outreach) {
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const result = await rpx.tracker.importRisTracker({
        courseId,
        outreachCsv: outreach.text,
        pipelineCsv: pipeline?.text,
        fallbackBetId: config.bets ? (fallbackBetId as UUID | undefined) : undefined,
      });
      rememberBet(courseId, unmatched ? fallbackBetId : undefined);
      showToast({
        type: 'ok',
        title: `Imported ${result.imported} ${result.imported === 1 ? 'person' : 'people'}`,
        message: '',
      });
      onImported();
      close();
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  const summary = preview
    ? [
        `${count} ${count === 1 ? 'person' : 'people'} found`,
        config.bets ? `${matched} matched to a bet` : '',
        preview.duplicates.length
          ? `${preview.duplicates.length} already in your tracker (skipped)`
          : '',
        preview.examples
          ? `${preview.examples} example ${preview.examples === 1 ? 'row' : 'rows'} skipped`
          : '',
        preview.noName
          ? `${preview.noName} ${preview.noName === 1 ? 'row' : 'rows'} with no name skipped`
          : '',
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Panel
      title="Import"
      close={close}
      footer={
        <>
          <BtnPrimary type="submit" form="tracker-import" disabled={!ready} isLoading={saving}>
            Import {count} {count === 1 ? 'person' : 'people'}
          </BtnPrimary>
          <BtnSecondary type="button" onClick={close}>
            Cancel
          </BtnSecondary>
        </>
      }
    >
      <form id="tracker-import" class="flex flex-col gap-6" onSubmit={save}>
        {picker}
        {!hasSequence && (
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Import needs an outreach sequence. This course doesn’t have one.
          </p>
        )}
        <section class="flex flex-col gap-2">
          <StepHeading n={1}>Outreach tab</StepHeading>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            In your sheet, open the Outreach tab, then File → Download → Comma-separated values
            (.csv). Choose that file here.
          </p>
          <CsvPicker id="import-outreach" fileName={outreach?.name} onFile={setOutreach} />
        </section>
        <section class="flex flex-col gap-2">
          <StepHeading n={2}>Pipeline tab (optional)</StepHeading>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            In your sheet, open the Pipeline tab, then File → Download → Comma-separated values
            (.csv). Choose that file here. Skip this if you have no conversations yet.
          </p>
          <CsvPicker id="import-pipeline" fileName={pipeline?.name} onFile={setPipeline} />
        </section>
        {preview && (
          <section class="flex flex-col gap-2">
            <StepHeading n={3}>Preview</StepHeading>
            {preview.fileError ? (
              <p class="text-sm text-red-700 dark:text-red-400">{preview.fileError}</p>
            ) : (
              <>
                <p class="text-sm text-gray-900 dark:text-gray-100">{summary}</p>
                {unmatched > 0 && (
                  <div class="flex flex-col gap-1">
                    <BetSelect
                      bets={data.bets}
                      value={fallbackBetId}
                      label={`Put the other ${unmatched} under`}
                      onChange={setFallbackBetId}
                    />
                    <span class={HELP_CLASS}>
                      Their offer from the sheet goes in the note, so nothing is lost.
                    </span>
                  </div>
                )}
                {tooMany && (
                  <p class="text-sm text-red-700 dark:text-red-400">
                    Import up to {MAX_IMPORT_PEOPLE} people at a time. Split the file and import the
                    rest after.
                  </p>
                )}
                <PreviewTable
                  headers={
                    config.bets
                      ? ['Name', 'Bet', 'Stage', 'Next step']
                      : ['Name', 'Stage', 'Next step']
                  }
                  rows={preview.people.map((p) => [
                    personName(p),
                    ...(config.bets ? [betOf(data.bets, p.betId)?.name ?? ''] : []),
                    stageLabel(config, p.stageKey),
                    p.nextStep
                      ? `${p.nextStep}${p.nextOn ? ` · ${fmtTrackerDate(p.nextOn, today)}` : ''}`
                      : '',
                  ])}
                />
                {preview.problems.length > 0 && (
                  <div class="flex flex-col gap-1">
                    <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {preview.problems.length}{' '}
                      {preview.problems.length === 1 ? 'row problem' : 'row problems'} (left out)
                    </h4>
                    <ul class="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300">
                      {preview.problems.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </section>
        )}
        <FieldError id="import-error" message={error} />
      </form>
    </Panel>
  );
}
